<template>
  <HpSidebar :id="id">
    <aside>
      <header class="my-7 grid place-content-center gap-5">
        <h2 class="text-center text-4xl">Monthly Drug Costs</h2>
        <p class="rounded-3xl bg-sky-100 p-4 text-center text-5xl leading-none">
          {{ formatUsd(_sumBy(monthlyCosts, (m) => m.total) / monthlyCosts.length) }}
          <span class="block text-base font-normal">Monthly Estimated Average</span>
        </p>
      </header>

      <HpAccordion>
        <HpAccordionItem
          v-for="month in monthlyCosts"
          :key="month.monthName"
          :title="month.monthName"
        >
          <template #header-detail>
            <div class="monthly-total">{{ formatUsd(month.total) }}</div>
          </template>
          <ul>
            <li
              class="flex justify-between gap-5 rounded-xl px-6 py-3 even:bg-gray-200/70"
              v-for="drug in month.drugs"
            >
              <h3
                v-html="drug.name.replaceAll('/', '<wbr>/</wbr>')"
                class="text-lg font-normal leading-snug"
              ></h3>
              <p class="leading-snug">
                {{ formatUsd(drug.cost) }}
              </p>
            </li>
          </ul>
        </HpAccordionItem>
      </HpAccordion>

      <footer class="my-8 rounded-3xl bg-gray-200 p-4">
        <h4 class="my-3 text-center text-3xl">3 Coverage Periods</h4>

        <ul class="mt-8 grid gap-7">
          <template v-for="period in coveragePeriods" :key="period.title">
            <li
              v-if="!period.shouldHide"
              class="grid gap-3 border-b border-border pb-7 last:border-none"
            >
              <div
                class="flex flex-col justify-between text-xl font-bold xs:flex-row xs:items-center"
              >
                <h5>{{ period.title }}</h5>
                <p>
                  <span class="font-normal">Limit: </span>
                  {{ period.limit }}
                </p>
              </div>
              <p v-html="period.explanation"></p>
            </li>
          </template>
        </ul>
      </footer>
    </aside>

    <template #footer>
      <div class="w-full border-t border-border pt-4">
        <div class="mx-auto grid w-full max-w-xs gap-2">
          <HpButton
            variant="secondary"
            icon="edit"
            @click.prevent="() => maybeDo(() => show('update-insurance-sidebar'))"
          >
            <span>Edit or Add Drugs</span>
          </HpButton>
          <HpButton @click="hide(id)">Close</HpButton>
        </div>
      </div>
    </template>
  </HpSidebar>
</template>

<script lang="ts" setup>
  import { QuotedPlanVm } from '~/models/quoting/QuotedPlan'

  const props = defineProps<{
    id: string
    quote: QuotedPlanVm
  }>()

  const { maybeDo } = ActionGuard.use()
  const { show, hide } = DialogController.use()

  const monthlyCosts = computed(
    () =>
      props.quote?.drugCoverage?.pharmacyCosts?.[0].monthlyCosts?.map((m) => ({
        monthName: m.monthName,
        total: _sumBy(m.details, (v) => v.memberCost ?? 0),
        drugs:
          m.details?.map((d) => ({
            name: props.quote.drugCoverage.coverageSummaries.find(
              (cs) => cs.externalId === d.externalId
            )?.drugName,
            cost: d.memberCost
          })) ?? []
      })) ?? []
  )

  const { getComponentContent, hydratePlaceholders } = Cms.use()

  const { content } = await getComponentContent('DrugMonthlyBreakdownSidebar', '2026')

  const limits = computed(() => content.value.data[props.quote.details.year?.toString() ?? '2026'])

  const coveragePeriods = computed(() => [
    {
      title: 'Annual Deductible',
      limit: formatUsd(props.quote.details.drugDeductible!),
      explanation: content.value.ext.annual_deductible_explanation,
      shouldHide: false
    },
    {
      title: 'Initial',
      limit: formatUsd(limits.value.initial),
      explanation: content.value.ext.initial_explanation,
      shouldHide: false
    },
    // {
    //   title: 'Gap (or Donut Hole)',
    //   limit: formatUsd(props.quote.details.catastrophicLimit),
    //   explanation: content.value.ext.donut_hole_explanation,
    //   shouldHide: true
    // },
    {
      title: 'Catastrophic',
      limit: 'End of plan year',
      explanation: hydratePlaceholders(content.value.ext.catastrophic_explanation, {
        catastrophicLimit: formatUsd(limits.value.catastrophicLimit)
      }),
      shouldHide: false
    }
  ])
</script>

<style scoped>
  :deep(.disclosure-outer) {
    @apply mb-0 rounded-none border-none;

    .disclosure-header-outer {
      @apply items-center py-3;
    }

    .disclosure-header-title {
      @apply !text-2xl !font-bold !tracking-normal;
    }

    .disclosure-header-detail {
      @apply !text-2xl !font-bold !tracking-tight !text-primary-900;
    }

    .disclosure-toggle {
      @apply ml-2 rounded-full bg-sky-100 p-1.5;

      :deep(svg) {
        @apply scale-125;
      }
    }
  }
</style>
